import React, { useMemo, useState } from 'react'
import { useNavigate, useParams, Navigate } from 'react-router-dom'
import { Timeline, Tag, Modal, Form, Input, Button as AntdButton, Typography } from 'antd'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { documentsApi, approvalsApi } from '@/api/services'
import type { Approval, UpdateApprovalRequest } from '@/types'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/Button'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import { isPodTransporter } from '@/utils/authPaths'
import { transporterDocumentJourneyPath } from '@/utils/transporterNav'
import { PageShell, PageHero, DashboardPanel } from '@/components/dashboard'
import { Route } from 'lucide-react'

const { TextArea } = Input
const { Text } = Typography

function statusTag(status: string, t: any) {
  const color = status === 'Approved' ? 'green' : status === 'Rejected' ? 'red' : 'orange'
  return <Tag color={color}>{t(`DocumentJourney:status.${status}`, status)}</Tag>
}

function getAttachmentHref(h: Approval): string | null {
  const anyH = h as any
  return (h.newUrl || h.url || anyH.tempUrl) ?? null
}

function isPdf(url: string) {
  return url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('application/pdf')
}function parseSapSnapshot(raw: string | null | undefined): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'string') return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    /* ignore */
  }
  return null
}

function formatSnapshotRow(v: unknown): React.ReactNode {
  if (v == null || v === '') return '—'
  if (typeof v === 'object') {
    return (
      <pre className="mt-1 max-h-32 overflow-auto rounded border border-border bg-muted/40 p-2 text-xs whitespace-pre-wrap">
        {JSON.stringify(v, null, 2)}
      </pre>
    )
  }
  return String(v)
}

type DocumentJourneyDetailPageProps = {
  transporterScoped?: boolean
}

const DocumentJourneyDetailPage: React.FC<DocumentJourneyDetailPageProps> = ({ transporterScoped }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { documentNo } = useParams()
  const { user } = useAuthStore()
  const isTransporterUser = isPodTransporter(user)
  const isTransporterView = transporterScoped ?? isTransporterUser
  const canApprove = !isTransporterView && (user?.permissions?.includes('pod.approve') ?? false)

  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [approvalModalVisible, setApprovalModalVisible] = useState(false)
  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(null)
  const [decisionPreset, setDecisionPreset] = useState<'Approved' | 'Rejected' | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['document-history', documentNo],
    queryFn: () => documentsApi.getHistory(String(documentNo)),
    select: (res: any) => res.data?.data,
    enabled: Boolean(documentNo),
  })

  const rows: Approval[] = data ?? []

  const latestSap = useMemo(() => {
    const latest = rows[rows.length - 1]
    return parseSapSnapshot((latest as { sapResponseJson?: string | null } | undefined)?.sapResponseJson)
  }, [rows])

  const latestSapEntries = useMemo(() => {
    if (!latestSap) return []
    return Object.keys(latestSap)
      .sort((a, b) => a.localeCompare(b))
      .map((k) => [k, latestSap[k]] as const)
  }, [latestSap])

  const header = useMemo(() => {
    const latest = rows[rows.length - 1]
    return {
      types: Array.from(new Set(rows.map((r) => r.type))).filter(Boolean),
      customerName: latest?.customerName ?? null,
      fromLocation: latest?.fromLocation ?? null,
      toLocation: latest?.toLocation ?? null,
      count: rows.length,
    }
  }, [rows])

  const updateApprovalMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateApprovalRequest }) =>
      approvalsApi.updateApproval(id, data),
    onSuccess: () => {
      toast.success(t('DocumentJourney:messages.updateSuccess'))
      setApprovalModalVisible(false)
      setSelectedApproval(null)
      setDecisionPreset(null)
      form.resetFields()
      queryClient.invalidateQueries({ queryKey: ['document-history', documentNo] })
      queryClient.invalidateQueries({ queryKey: ['documents-journey'] })
      queryClient.invalidateQueries({ queryKey: ['approvals'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('DocumentJourney:messages.updateFailed'))
    },
  })

  const openDecisionModal = (record: Approval, preset: 'Approved' | 'Rejected') => {
    setSelectedApproval(record)
    setDecisionPreset(preset)
    form.setFieldsValue({ remarks: '' })
    setApprovalModalVisible(true)
  }

  const handleApprovalSubmit = (values: { remarks: string }) => {
    if (!selectedApproval || !decisionPreset) return

    updateApprovalMutation.mutate({
      id: selectedApproval.id,
      data: {
        status: decisionPreset,
        remarks: values.remarks,
      },
    })
  }

  if (isTransporterUser && !transporterScoped && documentNo) {
    return <Navigate to={transporterDocumentJourneyPath(documentNo)} replace />
  }

  return (
    <PageShell>
      <PageHero
        title={t('DocumentJourney:header.title')}
        subtitle={t('DocumentJourney:header.detailDesc', { docNo: documentNo, count: header.count })}
        badges={[{ label: 'Journey', accent: true, icon: Route }]}
        actions={
          <Button
            variant="outline"
            onClick={() => navigate(isTransporterView ? transporterDocumentJourneyPath() : '/documentJourney')}
          >
            {t('DocumentJourney:actions.back')}
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <DashboardPanel
          title={t('DocumentJourney:details.cardSummaryTitle')}
          subtitle={t('DocumentJourney:details.cardSummaryDesc')}
          className="lg:col-span-1"
        >
          <div className="space-y-3 text-sm">
              <div>
                <div className="text-muted-foreground">{t('DocumentJourney:details.types')}</div>
                <div className="mt-1 flex flex-wrap gap-2">
                  {header.types.map((t) => (
                    <Tag
                      key={t}
                      color={t === 'ALT' ? 'blue' : t === 'DHR' ? 'green' : t === 'GRN' ? 'orange' : 'purple'}
                    >
                      {t}
                    </Tag>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">{t('DocumentJourney:details.detailCustomer')}</div>
                <div className="mt-1 text-foreground">{header.customerName ?? '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">{t('DocumentJourney:details.detailFrom')}</div>
                <div className="mt-1 text-foreground">{header.fromLocation ?? '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">{t('DocumentJourney:details.detailTo')}</div>
                <div className="mt-1 text-foreground">{header.toLocation ?? '—'}</div>
              </div>
              {latestSapEntries.length ? (
                <div className="border-t border-border pt-3 space-y-3">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('DocumentJourney:details.sapSnapshot')}
                  </div>
                  <div className="max-h-[40vh] space-y-3 overflow-y-auto pr-1">
                    {latestSapEntries.map(([k, v]) => (
                      <div key={k}>
                        <div className="text-muted-foreground">{k}</div>
                        <div className="mt-1 text-foreground break-words">{formatSnapshotRow(v)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
          </div>
        </DashboardPanel>

        <DashboardPanel
          title={t('DocumentJourney:details.cardHistoryTitle')}
          subtitle={t('DocumentJourney:details.cardHistoryDesc')}
          className="lg:col-span-2"
        >
          {isLoading ? (
            <div className="skeleton h-32 rounded-lg" />
          ) : (
            <Timeline
                  items={rows.map((h) => {
                    const href = getAttachmentHref(h)
                    return {
                      color: h.status === 'Approved' ? 'green' : h.status === 'Rejected' ? 'red' : 'orange',
                      children: (
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            {statusTag(h.status, t)}
                            <Tag>{h.type}</Tag>
                            <span className="text-xs text-muted-foreground">
                              {dayjs(h.createdAt).format('DD MMM YYYY, HH:mm')}
                            </span>
                            <span className="text-xs text-muted-foreground">ID {h.id}</span>
                          </div>
                          <div className="text-sm text-muted-foreground">{h.approval_remark || h.rejection_remark || '—'}</div>

                          {h.status === 'Pending' && canApprove ? (
                            <div className="flex flex-wrap gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="bg-background"
                                onClick={() => openDecisionModal(h, 'Approved')}
                              >
                                <span className="inline-flex items-center gap-2">
                                  <CheckOutlined />
                                  {t('DocumentJourney:actions.approve')}
                                </span>
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="bg-background"
                                onClick={() => openDecisionModal(h, 'Rejected')}
                              >
                                <span className="inline-flex items-center gap-2">
                                  <CloseOutlined />
                                  {t('DocumentJourney:actions.reject')}
                                </span>
                              </Button>
                            </div>
                          ) : null}

                          {/* Inline attachment preview (no extra click) */}
                          {href ? (
                            <div className="rounded-md border border-border bg-background overflow-hidden">
                              {isPdf(href) ? (
                                <iframe
                                  src={href}
                                  className="w-full h-[70vh]"
                                  title={`Attachment ${h.id}`}
                                />
                              ) : (
                                <img
                                  src={href}
                                  loading="lazy"
                                  className="w-full max-h-[70vh] object-contain bg-background"
                                  alt={`Attachment ${h.id}`}
                                />
                              )}
                            </div>
                          ) : null}
                        </div>
                      ),
                    }
                  })}
                />
          )}
        </DashboardPanel>
      </div>

      <Modal
        title={decisionPreset === 'Approved' ? t('DocumentJourney:modal.approveTitle') : t('DocumentJourney:modal.rejectTitle')}
        open={approvalModalVisible}
        onCancel={() => {
          setApprovalModalVisible(false)
          setSelectedApproval(null)
          setDecisionPreset(null)
          form.resetFields()
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        {selectedApproval && (
          <Form form={form} layout="vertical" onFinish={handleApprovalSubmit}>
            <div className="mb-4 rounded-lg border border-border bg-muted/30 p-4">
              <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                <div>
                  <Text strong>{t('DocumentJourney:form.docNo')}</Text> {selectedApproval.documentNo}
                </div>
                <div>
                  <Text strong>{t('DocumentJourney:form.formType')}</Text> <Tag color="blue">{selectedApproval.type}</Tag>
                </div>
                <div>
                  <Text strong>{t('DocumentJourney:form.formCustomer')}</Text> {selectedApproval.customerName}
                </div>
                <div>
                  <Text strong>{t('DocumentJourney:form.quantity')}</Text> {selectedApproval.quantity}
                </div>
              </div>
            </div>

            <Form.Item
              name="remarks"
              label={t('DocumentJourney:form.remarksLabel')}
              rules={[{ required: true, message: t('DocumentJourney:form.remarksRequired') }]}
            >
              <TextArea rows={4} placeholder={t('DocumentJourney:form.remarksPlaceholder')} />
            </Form.Item>

            <div className="flex justify-end space-x-2">
              <AntdButton
                onClick={() => {
                  setApprovalModalVisible(false)
                  setSelectedApproval(null)
                  setDecisionPreset(null)
                  form.resetFields()
                }}
              >
                {t('DocumentJourney:form.btnCancel')}
              </AntdButton>
              <AntdButton type="primary" htmlType="submit" loading={updateApprovalMutation.isPending}>
                {t('DocumentJourney:form.btnSubmit')}
              </AntdButton>
            </div>
          </Form>
        )}
      </Modal>
    </PageShell>
  )
}

export default DocumentJourneyDetailPage

